import { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { selectIsAuthenticated } from '@/features/auth/authSlice';
import { selectWalletBalance, selectWalletSummary } from '@/features/wallet/walletSlice';
import { analytics } from '@/services/analytics';
import { Gift, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { discountApi } from '@/services/discountApi';
import { useToast } from '@/hooks/use-toast';
import { logging } from '@/services/logging';
import { loadWalletSummary } from '@/features/wallet/walletSlice';
import { REGISTRATION_DISCOUNT_PROGRAM_ENABLED } from '@/config/featureFlags';

interface DiscountBannerProps {
  onRegisterClick: () => void;
}

const DiscountBanner: FC<DiscountBannerProps> = ({ onRegisterClick }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector((state) => state.auth.user);
  const walletBalance = useAppSelector(selectWalletBalance);
  const walletSummary = useAppSelector(selectWalletSummary);
  const [isVisible, setIsVisible] = useState(true);
  const [isApplying, setIsApplying] = useState(false);
  const referralCode = typeof window !== 'undefined' ? localStorage.getItem('tulasi.referralCode') : null;
  const canApplyRegistrationDiscount = REGISTRATION_DISCOUNT_PROGRAM_ENABLED;

  // Reset visibility when user changes (login/logout)
  useEffect(() => {
    setIsVisible(true);
    // Also reset applying state just in case
    setIsApplying(false);
  }, [isAuthenticated, user?.id]);

  // If user is logged in, we only show it if they don't have an active discount already.
  const hasLoadedWalletSummary = walletSummary.status === 'succeeded';
  const hasActiveDiscount = isAuthenticated && !!walletSummary.data?.activeDiscount;
  const shouldShow = isVisible && (
    (!isAuthenticated && canApplyRegistrationDiscount) ||
    (isAuthenticated && hasLoadedWalletSummary && walletBalance === 0 && !hasActiveDiscount && (canApplyRegistrationDiscount || !!referralCode))
  );

  if (!shouldShow) return null;

  const handleAction = async () => {
    if (isAuthenticated) {
      analytics.track('ui.discount_banner_apply_click');
      
      setIsApplying(true);
      try {
        let result;
        if (referralCode) {
          try {
            result = await discountApi.applyReferralCode(referralCode);
          } catch (apiError: any) {
            result = apiError.response?.data || { success: false, message: 'Failed to apply referral code' };
          }
        } else if (canApplyRegistrationDiscount) {
          // If no referral code, apply initial 10% discount via new specialized endpoint
          try {
            result = await discountApi.applyInitialDiscount();
          } catch (apiError: any) {
            result = apiError.response?.data || { success: false, message: 'Failed to apply initial discount' };
          }
        } else {
          result = { success: false, message: 'Registration discount program is disabled' };
        }

        if (result.success) {
          toast({
            title: t('discount.success.applied', '10% discount applied successfully!'),
          });
          analytics.track('discount_applied', { 
            type: 'INITIAL_10',
            hasReferralCode: !!referralCode,
            source: 'discount_banner' 
          });
          
          if (referralCode) {
            localStorage.removeItem('tulasi.referralCode');
          }
          
          // Refresh wallet to show updated discount info if any
          dispatch(loadWalletSummary());
          
          // 1) Hide the banner after successful application
          setIsVisible(false);
        } else {
          // 2) Handle case where user already has such discount or bigger
          const message = result.message || t('discount.error.applyFailed', 'Failed to apply discount.');
          toast({
            title: message,
            variant: 'destructive',
          });
          
          // If the failure reason is that they already have it, we might also want to hide the banner
          // or at least stop showing the "Apply" button as primary action.
          if (message.includes('already have') || result.reason === 'ACTIVE_DISCOUNT_EXISTS') {
            setIsVisible(false);
          }
          
          logging.warn('Discount banner: Discount not applied:', { message });
        }
      } catch (error) {
        toast({
          title: t('discount.error.applyFailed', 'Failed to apply discount.'),
          variant: 'destructive',
        });
        logging.error('Discount banner: Failed to apply discount:', { error });
      } finally {
        setIsApplying(false);
      }
    } else {
      analytics.track('ui.discount_banner_register_click');
      onRegisterClick();
    }
  };

  return (
    <div className={cn(
      "w-full bg-brand-tan border-b border-brand-tan py-1.5 px-4 transition-all duration-300 animate-in fade-in slide-in-from-top-4",
      "dark:bg-amber-700 dark:border-amber-600"
    )}>
      <div className="mx-auto flex max-w-7xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 sm:items-center">
          <div className="bg-white/20 p-1 rounded-full dark:bg-black/20">
            <Gift className="h-4 w-4 text-white dark:text-amber-200" />
          </div>
          <p className="text-sm font-semibold leading-snug text-white dark:text-gray-100">
            {isAuthenticated 
              ? t('homepage.applyDiscountNotification')
              : t('homepage.initialDiscountNotification')
            }
          </p>
        </div>
        
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button 
            variant="secondary" 
            size="sm"
            onClick={handleAction}
            disabled={isApplying}
            className="h-7 px-3 text-xs bg-white text-brand-brown hover:bg-white/90 border-none shadow-sm dark:bg-amber-100 dark:text-amber-900"
          >
            {isApplying ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              isAuthenticated ? t('common.apply', 'Apply') : t('auth.register', 'Register')
            )}
          </Button>
          <button 
            onClick={() => setIsVisible(false)}
            className="p-1 hover:bg-black/10 rounded-full transition-colors dark:hover:bg-white/10"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5 text-white/80 dark:text-gray-300" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscountBanner;
